import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../components/ui';

/**
 * Superadmin dashboard — one call to /admin/dashboard for the four panes:
 * expanded stats, "needs attention" digest, financial summary, system health.
 * Plus a Quick actions bar and the RAG reindex maintenance card.
 */
export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { loading, error, data, reload } = useAsync(() => api.adminDashboard(), []);

  return (
    <div className="page">
      <div className="perm-banner">
        🔓 You're signed in as <b>superadmin</b> — admin pages are unlocked because <code>me.isAdmin === true</code>.
      </div>
      <PageHead
        title="Admin dashboard"
        sub="Platform-wide overview"
        actions={
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={() => navigate('/admin/courses')}>+ New course</button>
            <button className="btn btn-sm" onClick={() => navigate('/admin/practices')}>+ New practice</button>
            <button className="btn btn-sm" onClick={() => navigate('/admin/members')}>Members</button>
            <button className="btn btn-sm" onClick={() => navigate('/admin/moderation')}>Moderation</button>
          </div>
        }
      />

      {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
        <>
          <StatCards s={data.stats} />
          <div className="grid-2">
            <AttentionCard items={data.attention} onGo={navigate} />
            <FinancialCard f={data.financial} />
          </div>
          <HealthCard checks={data.health} />
          <ReindexCard />
        </>
      )}
    </div>
  );
}

// ---- Stats -----------------------------------------------------------------

function StatCards({ s }) {
  const totalPendingInvites =
    (s.pendingGroupInvites || 0)
    + (s.pendingCourseInvites || 0)
    + (s.pendingPracticeInvites || 0)
    + (s.pendingEventInvites || 0);
  const rows = [
    { l: 'Members',       n: s.members },
    { l: 'Publishers',    n: s.publishers },
    { l: 'Event Leaders', n: s.eventLeaders },
    { l: 'Courses',       n: s.coursesPublished, d: `${s.coursesDraft} draft` },
    { l: 'Practices',     n: s.practicesPublished, d: `${s.practicesDraft} draft` },
    { l: 'Upcoming events', n: s.upcomingEvents },
    { l: 'Pending invites', n: totalPendingInvites,
      d: `G ${s.pendingGroupInvites} · C ${s.pendingCourseInvites} · P ${s.pendingPracticeInvites} · E ${s.pendingEventInvites}` },
  ];
  return (
    <div className="stat-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
      {rows.map((r) => (
        <div key={r.l} className="stat-card">
          <div className="l">{r.l}</div>
          <div className="n">{r.n ?? 0}</div>
          {r.d ? <div className="d">{r.d}</div> : null}
        </div>
      ))}
    </div>
  );
}

// ---- Needs attention -------------------------------------------------------

function AttentionCard({ items, onGo }) {
  return (
    <Section title="Needs attention">
      {(!items || items.length === 0)
        ? <Empty>All clear — nothing needs your attention right now.</Empty>
        : items.map((it) => (
            <div key={it.kind} className="member-row">
              <div style={{ flex: 1 }}>
                <div className="nm">{it.label}</div>
                <div className="em">{it.count} item{it.count === 1 ? '' : 's'}</div>
              </div>
              <button className="btn btn-sm" onClick={() => onGo(it.href)}>Review</button>
            </div>
          ))}
    </Section>
  );
}

// ---- Financial -------------------------------------------------------------

function fmtUsd(cents) {
  const dollars = (cents || 0) / 100;
  try { return dollars.toLocaleString(undefined, { style: 'currency', currency: 'usd' }); }
  catch { return `$${dollars.toFixed(2)}`; }
}
function FinancialCard({ f }) {
  return (
    <Section title="Financial">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div className="stat-card"><div className="l">Revenue · all time</div><div className="n">{fmtUsd(f.revenueCentsTotal)}</div></div>
        <div className="stat-card"><div className="l">Revenue · this month</div><div className="n">{fmtUsd(f.revenueCentsThisMonth)}</div></div>
      </div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>
        Paid course enrollments · {f.paidCourseEnrollmentsTotal} total ({f.paidCourseEnrollmentsThisMonth} this month)
      </div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>
        Paid practice enrollments · {f.paidPracticeEnrollmentsTotal} total ({f.paidPracticeEnrollmentsThisMonth} this month)
      </div>
    </Section>
  );
}

// ---- System health --------------------------------------------------------

function HealthCard({ checks }) {
  const color = (s) => (s === 'ok' ? 'var(--green-text, #15803d)'
    : s === 'warn' ? '#b45309'
    : s === 'error' ? '#b91c1c' : 'var(--muted)');
  const dot = (s) => ({
    display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
    background: color(s), marginRight: 8, verticalAlign: 'middle',
  });
  return (
    <Section title="System health">
      {(!checks || checks.length === 0) ? <Empty>No checks reported.</Empty> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
          {checks.map((c) => (
            <div key={c.key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
              <div style={{ fontWeight: 600 }}>
                <span style={dot(c.status)} />
                {c.label}
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--muted)', marginTop: 4 }}>{c.detail}</div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ---- RAG reindex (unchanged from previous dashboard) -----------------------

function ReindexCard() {
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  const poll = async () => {
    try {
      const p = await api.adminReindexStatus();
      setProgress(p);
      if (!p?.running && timer.current) { clearInterval(timer.current); timer.current = null; }
    } catch (e) { setErr(e.message); }
  };

  useEffect(() => {
    poll();
    return () => { if (timer.current) clearInterval(timer.current); };
  }, []);

  const start = async () => {
    if (!window.confirm('Re-embed ALL lessons and files against the current embedding model? This can take a while and runs in the background.')) return;
    setBusy(true); setErr(null);
    try {
      const p = await api.adminReindexAll();
      setProgress(p);
      if (!timer.current) timer.current = setInterval(poll, 2000);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const running = progress?.running;
  const line = (label, done, fail, total) =>
    `${label}: ${done}/${total}${fail ? ` (${fail} failed)` : ''}`;

  return (
    <Section title="AI index maintenance">
      <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontSize: '0.875rem' }}>
        Rebuild the RAG index for every lesson and uploaded file. Run this once after
        switching embedding models (see design/015), or any time the index looks stale.
      </p>
      {err ? <ErrorNote error={err} /> : null}
      {progress && (progress.startedAt || running) ? (
        <div style={{ margin: '0 0 12px', fontSize: '0.875rem', fontFamily: 'var(--mono, monospace)' }}>
          <div>{line('Lessons', progress.lessonsDone, progress.lessonsFailed, progress.lessonsTotal)}</div>
          <div>{line('Event files', progress.eventFilesDone, progress.eventFilesFailed, progress.eventFilesTotal)}</div>
          <div>{line('Group files', progress.groupFilesDone, progress.groupFilesFailed, progress.groupFilesTotal)}</div>
          <div style={{ marginTop: 6, color: running ? 'var(--indigo-600, #4f46e5)' : 'var(--green-text, #15803d)' }}>
            {running ? 'Running…' : progress.finishedAt ? 'Done.' : ''}
          </div>
        </div>
      ) : null}
      <button className="btn btn-primary" disabled={busy || running} onClick={start}>
        {running ? 'Reindexing…' : busy ? 'Starting…' : 'Rebuild AI index'}
      </button>
    </Section>
  );
}
